import { Grid, Typography } from '@mui/material';
import MFSelectField from '../../lib/formik/SelectField';
import { ProceedSaveLater, SubHeading } from '../investors/components';
import { MFTextField } from '../../lib/formik';
import { Formik, validateYupSchema, yupToFormErrors } from 'formik';
import { Applicant } from '../../redux-store/types/api-types';
import {
  grossAnnualMasters,
  investorTypeMasters,
  PEPsMasters,
  USER_ROLES,
  yesAndNoMasters,
} from '../../utils/constant';
import { DatePicker } from '../../lib/formik/DatePicker';
import React, { useEffect, useState } from 'react';
import {
  applicationComparison,
  currencyConversion,
  removeSingleQuote,
  saveForLater,
  minNetworthDate,
  checkValidationBasedOnDate,
} from '../../utils/utilityFunctions';
import { useHistory } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { RootStateType } from '../../redux-store/reducers';
import { updateApplication } from '../../redux-store/actions/application';
import { NonIndividualContributorValidationSchema } from '../../utils/schema';
import { useSnackbar } from 'notistack';
import { DematDetails } from '../investors/additionalKYCDetails';
import { useFeatureToggles } from '../../utils/toggler';
import { UseRadioGroup } from '../../lib/formik/Radio';

const contributorObject: Partial<Applicant> = {
  name: '',
  cityOfIncorporation: '',
  ckycNo: '',
  investorType: '',
  dateOfBirth: '',
  panNo: '',
  grossAnnualIncome: '',
  netWorth: '',
  netWorthDate: '',
  politicallyExposedPersonStatus: '',
  corporateIdentificationNo: '',
  kinNo: '',
  isExistingRelationship: '',
  existingRelationshipNo: '',
};

export type Values = {
  saveType: string;
  modeOfHolding: string;
  applicants: Partial<Applicant>[];
  dpName: string;
  dpId: string;
  dpClientId: string;
  createdAt: string;
};

const initialValues: Values = {
  modeOfHolding: 'single',
  applicants: [contributorObject],
  dpName: '',
  dpId: '',
  dpClientId: '',
  saveType: 'save and proceed',
  createdAt: '',
};

export default function ContributorDetails(): JSX.Element {
  const { application } = useSelector((store: RootStateType) => store.application);
  const [contributorValues, setContributorValues] = useState(initialValues);
  const history = useHistory();
  const dispatch = useDispatch();
  const { role = '' } = useSelector((store: RootStateType) => store.auth);
  const [loading, setLoading] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  useEffect(() => {
    try {
      const {
        applicants = [],
        dpClientId = '',
        dpId = '',
        dpName = '',
        createdAt = '',
      } = application || {};
      setContributorValues({
        ...contributorValues,
        applicants: applicants.length
          ? applicants.map((applicant) => ({
              ...applicant,
              isExistingRelationship: applicant.isExistingRelationship || '',
              existingRelationshipNo: applicant.existingRelationshipNo || '',
            }))
          : [contributorObject],
        dpClientId,
        dpName,
        dpId,
        createdAt,
      });
    } catch (e) {
      console.error((e as Error).message);
    }
  }, [application]);

  const dateFeatureToggles = useFeatureToggles();

  const handleApplicant = async (values: Values) => {
    const { saveType, applicants = [], dpClientId, dpId, dpName } = values;
    const { id, currentStep, banks = [] } = application || {};
    // const isAllBanksPennyChecked = banks.length
    //   ? banks.every((bank) => bank.pennydropCheck)
    //   : false;
    // const contributorDetailsFieldsCheck = application?.applicants
    //   .map((applicant, index) => {
    //     return applicants.map((_applicant, ind) => {
    //       if (index === ind) {
    //         const { investorType } = _applicant;
    //         if (investorType !== applicant.investorType) {
    //           return 'true';
    //         }
    //         return 'false';
    //       }
    //       return 'false';
    //     });
    //   })
    //   .flat();
    const checkApplication = applicationComparison(
      {
        ...application,
        applicants: application?.applicants
          .map((applicant) => ({
            ...applicant,
            name: removeSingleQuote(applicant.name),
            cityOfIncorporation: removeSingleQuote(applicant.cityOfIncorporation),
          }))
          ?.sort((applicant1, applicant2) => Number(applicant1.id) - Number(applicant2.id)),
      },
      {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        ...application!,
        banks:
          values.applicants[0].name?.split("'").join('').trim() ===
          application?.applicants[0].name?.split("'").join('').trim()
            ? banks
            : banks.map((bank) => ({
                ...bank,
                pennydropCheck: false,
                pennydropVerified: false,
              })),
        applicants: values.applicants
          .map((applicant) => ({
            ...applicant,
            name: removeSingleQuote(applicant.name),
            cityOfIncorporation: removeSingleQuote(applicant.cityOfIncorporation),
            existingRelationshipNo:
              yesAndNoMasters[applicant.isExistingRelationship || ''] === yesAndNoMasters.yes
                ? applicant.existingRelationshipNo
                : '',
          }))
          .sort((applicant1, applicant2) => Number(applicant1.id) - Number(applicant2.id)),
        modeOfHolding: 'single',
        dpClientId,
        dpId,
        dpName,
        currentStep: !!currentStep && currentStep > 1 ? currentStep : Number(currentStep) + 1,
      }
    );

    if (id && !checkApplication) {
      await dispatch(
        updateApplication({
          body: {
            ...application,
            banks:
              values.applicants[0].name?.split("'").join('').trim() ===
              application?.applicants[0].name?.split("'").join('').trim()
                ? banks
                : banks.map((bank) => ({
                    ...bank,
                    pennydropCheck: false,
                    pennydropVerified: false,
                  })),
            applicants: values.applicants.map((applicant) => ({
              ...applicant,
              name: removeSingleQuote(applicant.name),
              cityOfIncorporation: removeSingleQuote(applicant.cityOfIncorporation),
              existingRelationshipNo:
                yesAndNoMasters[applicant.isExistingRelationship || ''] === yesAndNoMasters.yes
                  ? applicant.existingRelationshipNo
                  : '',
            })),
            modeOfHolding: 'single',
            dpClientId,
            dpName,
            dpId,
            currentStep: 2,
            // !!currentStep && currentStep > 1
            //   ? (values.applicants[0].name?.trim() !== application?.applicants[0].name?.trim() &&
            //       isAllBanksPennyChecked) ||
            //     (contributorDetailsFieldsCheck as unknown as string[])?.includes('true')
            //     ? 2
            //     : currentStep
            //   : (values.applicants[0].name?.trim() !== application?.applicants[0].name?.trim() &&
            //       isAllBanksPennyChecked) ||
            //     (contributorDetailsFieldsCheck as unknown as string[])?.includes('true')
            //   ? 2
            //   : Number(currentStep) + 1,
          },
          applicationId: id,
          ...(saveType !== 'save and proceed' && {
            toastMessage: '',
          }),
        })
      );
    }
  };

  const handleSubmit = async (values: Values) => {
    try {
      setLoading(true);
      const { id, applicant1ReferenceId = '', applicationNumber } = application || {};
      const { saveType } = values;
      await handleApplicant(values);
      saveType === 'save and proceed'
        ? history.push('contact-details', { id, applicant1ReferenceId })
        : history.push(saveForLater(role, id, applicant1ReferenceId));
      if (saveType !== 'save and proceed') {
        enqueueSnackbar(`Application ${applicationNumber} - ` + ' Saved successfully', {
          variant: 'success',
          autoHideDuration: 3000,
        });
      }
      //history.push(`document-details`, { applicationType: 'non_individual' });
    } catch (e) {
      setLoading(false);
      console.error((e as Error).message);
    }
  };

  const isFieldDisabledForPOALogin = [USER_ROLES.POAAPPROVER].includes(role);
  return (
    <Formik
      initialValues={contributorValues}
      validate={(values: Values) => {
        try {
          validateYupSchema(
            values,
            NonIndividualContributorValidationSchema(
              dateFeatureToggles?.dematFieldValidation,
              dateFeatureToggles?.existingRelValidationDate
            ),
            true,
            values
          );
        } catch (e) {
          return yupToFormErrors(e);
        }
      }}
      onSubmit={handleSubmit}
      enableReinitialize={true}>
      {({ handleSubmit, values, setValues, setFieldValue }) => (
        <>
          <Grid
            container
            rowSpacing={1}
            //columnSpacing={5}
            sx={{
              width: '100%',
              ml: 0,
              '.MuiGrid-item': { px: { xs: 0, sm: '30px' } },
            }}
            component="form"
            noValidate
            onSubmit={handleSubmit}>
            {values.applicants.map((applicant, index) => (
              <React.Fragment key={index}>
                <Grid item xs={12} sm={6}>
                  <MFTextField
                    name={`applicants.${index}.name`}
                    label="Name of Entity"
                    placeholder="Enter Name of Entity"
                    disabled={applicant.dataFetchedFromKRA || isFieldDisabledForPOALogin || false}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label={'Date of Registration/Incorporation'}
                    inputLabelStyles={{
                      transform: 'unset',
                      fontSize: 14,
                      fontWeight: 500,
                      color: 'rgba(0,0,0,0.7)',
                    }}
                    required
                    placeholder={'DD/MM/YYYY'}
                    name={`applicants.${index}.dateOfBirth`}
                    disabled={applicant.dataFetchedFromKRA || isFieldDisabledForPOALogin || false}
                    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MFTextField
                    name={`applicants.${index}.cityOfIncorporation`}
                    label="Place of Registration/Incorporation"
                    placeholder="Enter Place of Registration/Incorporation"
                    disabled={isFieldDisabledForPOALogin}
                    required
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MFTextField
                    name={`applicants.${index}.ckycNo`}
                    label="CKYC No./CKYC Acknowledge No."
                    placeholder="Enter CKYC No./CKYC Acknowledge No."
                    disabled={isFieldDisabledForPOALogin}
                    required
                  />
                </Grid>
                {/* <Grid item xs={12} sm={6}>
                  <MFTextField
                    name={`applicants.${index}.kinNo`}
                    label="KIN No"
                    placeholder="Enter KIN No"
                  />
                </Grid> */}
                <Grid item xs={12} sm={6}>
                  <MFSelectField
                    name={`applicants.${index}.investorType`}
                    label="Investor Type"
                    items={Object.keys(investorTypeMasters).map((investor) => ({
                      key: investorTypeMasters[investor],
                      value: investor,
                    }))}
                    required
                    disabled={isFieldDisabledForPOALogin}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MFTextField
                    name={`applicants.${index}.corporateIdentificationNo`}
                    label="Corporate Identification No "
                    placeholder="Enter Corporate Identification No"
                    disabled={isFieldDisabledForPOALogin}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MFTextField
                    name={`applicants.${index}.panNo`}
                    label="PAN"
                    placeholder="Enter PAN"
                    disabled={true}
                    required
                  />
                </Grid>
                {checkValidationBasedOnDate(
                  application?.createdAt || '',
                  dateFeatureToggles?.existingRelValidationDate
                ) && (
                  <>
                    <Grid item xs={12} sm={6} sx={{ mt: 2 }}>
                      <UseRadioGroup
                        name={`applicants.${index}.isExistingRelationship`}
                        formLabel="Existing Relationship"
                        disabled={isFieldDisabledForPOALogin}
                        items={Object.keys(yesAndNoMasters).map((item) => ({
                          label: yesAndNoMasters[item],
                          value: item,
                        }))}
                        required
                        onChange={({ target: { value } }) => {
                          setFieldValue(`applicants.${index}`, {
                            ...values.applicants[index],
                            isExistingRelationship: value,
                          });
                        }}
                      />
                    </Grid>
                    {yesAndNoMasters[values.applicants[index].isExistingRelationship || ''] ===
                      yesAndNoMasters.yes && (
                      <Grid item xs={12} sm={6}>
                        <MFTextField
                          name={`applicants.${index}.existingRelationshipNo`}
                          label={'Existing Relationship No'}
                          placeholder="Enter Existing Relationship No"
                          disabled={isFieldDisabledForPOALogin}
                          required
                        />
                      </Grid>
                    )}
                  </>
                )}
                <SubHeading
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}>
                  Other Details
                </SubHeading>
                <Grid item xs={12} sm={6}>
                  <MFSelectField
                    name={`applicants.${index}.grossAnnualIncome`}
                    label="Gross Annual Income"
                    items={Object.keys(grossAnnualMasters).map((grossIncome) => ({
                      key: grossAnnualMasters[grossIncome],
                      value: grossIncome,
                    }))}
                    required
                    disabled={isFieldDisabledForPOALogin}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <MFTextField
                    name={`applicants.${index}.netWorth`}
                    label="Net Worth"
                    placeholder="Enter Net Worth"
                    disabled={isFieldDisabledForPOALogin}
                    required
                  />
                  {values.applicants[index].netWorth &&
                    !isNaN(Number(values.applicants[index].netWorth)) && (
                      <Typography sx={{ fontSize: 13 }}>
                        {currencyConversion(values.applicants[index].netWorth)}
                      </Typography>
                    )}
                </Grid>
                <Grid item xs={12} sm={6}>
                  <DatePicker
                    label={'Net worth as on'}
                    inputLabelStyles={{
                      transform: 'unset',
                      fontSize: 14,
                      fontWeight: 500,
                      color: 'rgba(0,0,0,0.7)',
                    }}
                    required
                    placeholder={'Enter Net worth as on'}
                    name={`applicants.${index}.netWorthDate`}
                    value={values.applicants[index].netWorthDate}
                    minDate={minNetworthDate(application?.createdAt || '')}
                  />
                </Grid>
                {/* <Grid item xs={12} sm={6}>
                  <MFSelectField
                    name={`applicants.${index}.politicallyExposedPersonStatus`}
                    label="Politically Exposed Person (PEP) Status"
                    items={PEPsMasters.map((pep) => ({ key: pep, value: pep }))}
                    disabled={true}
                  />
                </Grid> */}
              </React.Fragment>
            ))}
            <DematDetails values={values} />
            <ProceedSaveLater
              saveLater={() => {
                setValues({
                  ...values,
                  applicants: values.applicants.map((applicant) => ({
                    ...applicant,
                  })),
                  saveType: 'save for later',
                });
              }}
              saveAndProceed={() => {
                setValues({
                  ...values,
                  applicants: values.applicants.map((applicant) => ({
                    ...applicant,
                  })),
                  saveType: 'save and proceed',
                });
              }}
              loader={loading}
              clickedButton={values.saveType}
            />
          </Grid>
        </>
      )}
    </Formik>
  );
}
